import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import get from 'lodash/get';

import { uploadTransfer as uploadTransferAction } from '../../../transfer-bundle/store/actions';
import {
  hasStarted,
  validateTransfer,
} from '../../../transfer-bundle/helpers/index';

import Fields from './fields';
import Files from './files';
import Options from './options';
import LockedRecipient from './locked-recipient';

import I18n from '@module/i18n';
import Analytics from '@module/analytics';
import LaunchDarkly from '@module/launchdarkly';

import Actions from '@app/store/action';
import Helper from '@app/helper';

import Icon from '@component/icon';
import Scrollable from '@component/scrollable';

import { SCIENCE_BLUE, DODGER_BLUE } from '@constant/colors';
import { EXPERIMENT_VALIDATE_LD_SP } from '@constant/experiments';
import { allowedSizeSelector } from '@app/store/selector/transfers';
import {
  getIsForwardedTransfer,
  getCurrentSize,
} from '@app/transfer-bundle/store/selectors';
import Poptip from '@app/component/info/poptip';

export class Form extends React.Component {
  state = {
    transferStarted: false,
  };

  constructor(props) {
    super(props);

    this.hideOptions = this.hideOptions.bind(this);
    this.hideOptionsOnScroll = this.hideOptionsOnScroll.bind(this);
    this.startTransfer = this.startTransfer.bind(this);
    this.handleLockedRecipientClick = this.handleLockedRecipientClick.bind(
      this
    );
  }

  componentDidMount() {
    if (this.props.transfer.state === 'downloadable') {
      this.props.create();
    }
  }

  toggleOptions = () => {
    const { ui } = this.props;
    if (ui.uploader.options.visible) {
      this.hideOptions();
    } else {
      this.props.setUploaderOptions({ visible: true });
    }

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_more_button',
    });
  };

  hideOptions() {
    if (!this.props.ui.uploader.options.visible) {
      return;
    }

    if (!this.optionsNode) {
      return;
    }

    // Calculate new scroll position
    const node = this.optionsNode.node;
    if (node) {
      const newTop = this.getScrollPosition() - node.offsetHeight;
      this.scrollTo(newTop, {
        animated: true,
        duration: 300,
        onScrollingEnd: () => {
          this.props.setUploaderOptions({ visible: false });
        },
      });
    }
  }

  hideOptionsOnScroll(e) {
    if (!this.props.ui.uploader.options.visible) {
      return;
    }

    if (!this.optionsNode) {
      return;
    }

    // If the element is (close to) out of view, hide it again.
    const node = this.optionsNode.node;
    if (node && e.scrollTop + e.clientHeight <= node.offsetTop + 5) {
      this.props.clearTransferNotification();
      this.props.setUploaderOptions({ visible: false });
    }
  }

  isDisabled() {
    const { session, transfer } = this.props;
    const { transferStarted } = this.state;
    return (
      !session.tandc ||
      hasStarted(transfer.state, transfer.id) ||
      transferStarted
    );
  }

  // Start the actual transfer, if it passes some checks.
  startTransfer() {
    const {
      clearTransferNotification,
      uploadTransfer,
      user,
      transfer,
      channel,
      account,
      session,
      allowedSize,
      notification,
      isForwardedTransfer,
    } = this.props;

    if (this.isDisabled()) {
      return false;
    }

    this.setState({ transferStarted: true }, () => {
      clearTransferNotification();

      uploadTransfer({
        channel,
        account,
        user,
        session,
        notification,
        allowedSize,
        isForwardedTransfer,
        currentSize: getCurrentSize({ transfer }),
      });

      const tracker = user.loggedIn
        ? 'WeTransfer_Site_Landing_Page_Overlay_WT_plus_send_file_Conversion'
        : 'WeTransfer_Site_Landing_Page_Overlay_WT_Free_send_file_Conversion';
      Analytics.trackWithPixelOrSDK(tracker);

      Analytics.trackSnowplowEvent({
        category: 'transfer',
        action: 'click_transfer',
      });

      // Send out the version, coupled to UI logic.
      Analytics.trackSnowplowEvent({
        category: 'validate_launchdarkly',
        action: 'start_transfer',
        label: LaunchDarkly.getVariant(EXPERIMENT_VALIDATE_LD_SP),
      });
    });
  }

  // Proxy scrollTo function
  scrollTo(...args) {
    if (!this.scrollable) {
      return false;
    }
    this.scrollable.scrollTo(...args);
  }

  getScrollPosition() {
    return this.scrollable.getScrollPosition();
  }

  getScrollHeight() {
    return this.scrollable.getScrollHeight();
  }

  componentDidUpdate(prevProps) {
    const { ui, transfer } = this.props;
    // Options visible? Show them in scroll view
    if (
      prevProps.ui.uploader.options.visible !== ui.uploader.options.visible &&
      ui.uploader.options.visible === true
    ) {
      this.scrollTo('bottom', {
        animated: true,
        duration: 400,
        blockEvents: true,
      });
    }

    // Transfer type switched? We're working in the options
    if (prevProps.transferType !== transfer.type) {
      this.scrollTo('bottom');
    }

    if (
      get(this, 'props.notification.transfer.text', false) &&
      this.state.transferStarted
    ) {
      this.setState({ transferStarted: false });
    }
  }

  renderFooter(isRecipientLocked) {
    const { ui, transfer, user, allowedSize, isForwardedTransfer } = this.props;
    const optionsColor = ui.uploader.options.visible
      ? SCIENCE_BLUE
      : DODGER_BLUE;

    let isTransferDisabled = this.isDisabled();
    let buttonLabel =
      transfer.type === 'email'
        ? I18n.t('transfer.buttons.transfer')
        : I18n.t('transfer.buttons.get_a_link');
    if (isForwardedTransfer) {
      buttonLabel = I18n.t('transfer.buttons.forward');
    }

    // If the transfer isn't valid, e.g. transfer is processing,
    // we won't activate the button
    try {
      validateTransfer({
        transfer,
        maximumNumberOfRecipients: user.rules.maximumNumberOfRecipients,
        maximumNumberOfFiles: user.rules.maximumNumberOfFiles,
        allowedSize,
        currentSize: getCurrentSize({ transfer }),
      });
    } catch (e) {
      isTransferDisabled = true;
    }

    const transferButtonBase = 'transfer__button';
    const buttonClassName = classnames(transferButtonBase, {
      [`${transferButtonBase}--inactive`]: isTransferDisabled,
    });

    if (
      !isRecipientLocked &&
      !user.loggedIn &&
      this.props.transfer.showTransferOptionsTooltip
    ) {
      const { changeType } = this.props;

      const transferOptionsLinkTooltip = (
        <React.Fragment>
          <p>{I18n.t('transfer.bubble.tips.prefer_link.text')}</p>
          <a
            className="popover__button"
            target="_self"
            onClick={() => changeType('link')}
          >
            {I18n.t('transfer.bubble.tips.prefer_link.button')}
          </a>
        </React.Fragment>
      );

      return (
        <div className="transfer__footer">
          <Poptip
            className="email-verification__transfer-options-tooltip"
            visible={true}
            content={transferOptionsLinkTooltip}
            anchor="bottom"
            offset={15}
            trigger="manual"
            setAutoHideTimer={true}
          >
            <Icon
              type="TransferOptions"
              onClick={this.toggleOptions}
              color={optionsColor}
            />
          </Poptip>
          <button
            type="button"
            disabled={isTransferDisabled}
            onClick={this.startTransfer}
            className={buttonClassName}
          >
            {buttonLabel}
          </button>
        </div>
      );
    }

    return (
      <div className="transfer__footer">
        <Icon
          type="TransferOptions"
          onClick={this.toggleOptions}
          color={optionsColor}
        />

        <button
          type="button"
          disabled={isTransferDisabled}
          onClick={this.startTransfer}
          className={buttonClassName}
        >
          {buttonLabel}
        </button>
      </div>
    );
  }

  handleLockedRecipientClick() {
    this.props.pushTransferNotification({
      text: 'LockedRecipient',
    });
  }

  render() {
    const { transfer, channel, user, account } = this.props;

    const isRecipientLocked = Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel.defaultRecipient,
      channelProfileReceivingLocked: channel.profileReceivingLocked,
      userLoggedIn: user.loggedIn,
      channelName: channel.name,
      accountSubDomainName: account.subdomain_name,
    });

    return (
      <div
        ref={(node) => (this.node = node)}
        className={`transfer__window uploader uploader--form uploader--type-${transfer.type}`}
      >
        <Scrollable
          ref={(node) => (this.scrollable = node)}
          className="transfer__contents"
          onScroll={this.hideOptionsOnScroll}
        >
          <Files
            transfer={transfer}
            fileCount={transfer.files.length}
            panel={this.node}
            panelComponent={this}
            onClick={this.hideOptions}
          />
          {isRecipientLocked && (
            <LockedRecipient
              recipient={channel.defaultRecipient}
              onClick={this.handleLockedRecipientClick}
            />
          )}
          <Fields
            onInteraction={this.hideOptions}
            transfer={transfer}
            type={transfer.type}
            fileCount={transfer.files.length}
            recipientCount={transfer.recipients.length}
            panel={this}
            isRecipientLocked={isRecipientLocked}
          />

          <Options
            ref={(node) => (this.optionsNode = node)}
            type={transfer.type}
            password={transfer.password}
            expiry={transfer.expiry}
            isRecipientLocked={isRecipientLocked}
          />
        </Scrollable>
        {this.renderFooter(isRecipientLocked)}
      </div>
    );
  }
}

Form.propTypes = {
  clearTransferNotification: PropTypes.func.isRequired,
  pushTransferNotification: PropTypes.func.isRequired,
  setUploaderOptions: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  changeType: PropTypes.func.isRequired,
  uploadTransfer: PropTypes.func.isRequired,
  transfer: PropTypes.object.isRequired,
  scrollTo: PropTypes.func,
  session: PropTypes.object.isRequired,
  notification: PropTypes.object,
  channel: PropTypes.object,
  user: PropTypes.object,
  transferType: PropTypes.string,
  account: PropTypes.object,
  ui: PropTypes.object,
  allowedSize: PropTypes.number,
  isForwardedTransfer: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  ui: state.ui,
  allowedSize: allowedSizeSelector(state),
  isForwardedTransfer: getIsForwardedTransfer(state),
  transfer: state.transfer,
});

const mapDispatchToProps = {
  clearTransferNotification: Actions.Notification.clearTransfer,
  pushTransferNotification: Actions.Notification.pushTransfer,
  create: Actions.Transfer.create,
  uploadTransfer: uploadTransferAction,
  setUploaderOptions: Actions.UI.setUploaderOptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(Form);
